import type {CheckoutServiceParticipant} from '../../../domain/types';
import {ServiceType} from '../../../domain/types';
import * as React from 'react';

import type { SheetVariant} from '@sunwebgroup/ui-components';
import {
  SpaceBetween,
  tk,
  Typography,
  ListItem,
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetClose,
  ToggleGroup,
  RadioGroup,
  SheetTitle,
  FormFieldControl,
  FormField, Button
} from '@sunwebgroup/ui-components';
import {Icon} from '@sunwebgroup/ui-components/uds';
import { useIntl } from '@/intl';
import {useService} from '@/scopes/checkout/domain/hooks';

import ServiceBox from '../ServiceBox';
import { isEmpty, values } from 'ramda';
import {
  clothingGenderSelector,
  clothingSizeSelector,
  clothingServiceEditingValueByIdSelector,
  clothingServiceOptionsListSelector,
} from './selectors';
import * as Popover from '@radix-ui/react-popover';
import {useBreakpoints} from '@/scopes/common/components/utils/breakpoints';
import {
  saveEditingClothingServiceValue,
  updateEditingClothingServiceGender,
  updateEditingClothingServiceSize,
  updateEditingClothingServiceValue,
} from "@/scopes/checkout/domain/actions/services/clothing";
import {
  GENDERS_ORDER,
  NO_GENDER,
  NO_SIZE,
  SIZES_ORDER
} from "@/scopes/checkout/components/services/ClothingService/constants";
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { createClothingServiceEvent } from '../../../domain/analytics';
import { useClickFilterClothingService } from '@/scopes/checkout/domain/analytics/useClickFilterClothingService';
import {
  clothingServiceIsBeingEdited,
  useClothingServiceStore,
} from '@/scopes/checkout/components/services/ClothingService/store';
import {
  ClothingContent, OpenSheetButton,
  OptionSelected,
  OptionSelectedTitle,
  ParticipantName,
  PopoverContent,
  PopoverTrigger,
  Price, PriceBlock,
  RoomName,
  SelectParticipantButton,
  SheetFilterLabel,
  SheetParticipantName,
} from '@/scopes/checkout/components/services/ClothingService/ClothingComponents';
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon';

export function TShirtService() {
  const intl = useIntl();
  const { service } = useService(ServiceType.TShirt);
  useSendAncillaryAvailable(service, 'selectTShirt');
  if (!service) return null;

  const { rooms, participants, options } = service

  if (isEmpty(participants)) return null;
  if (isEmpty(options)) return null;

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_clothing_tshirt_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_clothing_tshirt_description' })}
      glyph={getIconForService(ServiceType.TShirt)}
    >
      <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
        {rooms.map(({ id: roomId, name: roomName, participantIds }) => (
          <SpaceBetween key={roomId} direction="y" spacing={tk('spacing_md')}>
            { rooms.length > 1 && <RoomName as="p" $variant="body-sm-bold">
              {roomName}
            </RoomName>
            }
            <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
              {participantIds.map((participantId) => {
                const participant = participants?.[participantId];
                if (!participant || participant.availableOptions.length === 0) {
                  return null;
                }

                return (
                  <ClothingServicePerParticipant
                    {...participant}
                    key={participantId}
                  />
                );
              })}
            </SpaceBetween>
          </SpaceBetween>
        ))}
        {isEmpty(rooms) &&
          Object.values(participants ?? {}).map((participant) => (
            <ClothingServicePerParticipant {...participant} key={participant.id} />
          ))}

        <ParticipantMenuSelection />
      </SpaceBetween>
    </ServiceBox>
  );
}

function ParticipantMenuSelection() {
  const { service } = useService(ServiceType.TShirt);
  const [isParticipantMenuOpen, setIsParticipantMenuOpen] = React.useState(false);
  const intl = useIntl();
  const { sendEvent } = useAnalytics();
  const { startEditingService } = useClothingServiceStore();

  const handleClick = React.useCallback((participantId: string) => {
      if (!participantId) return;
      if (!service) return;
      const { participants } = service

      startEditingService({
        participantId,
        serviceType: ServiceType.TShirt,
        isEditing: true
      });

      if (isParticipantMenuOpen){
        setIsParticipantMenuOpen(false)
      }
      sendEvent(
        createClothingServiceEvent({
          result: 'Add t-shirt for',
          type: participants[participantId].name,
          description: 'TShirt',
          element: 'clickable text'
        })
      );
    },
    [sendEvent, isParticipantMenuOpen, startEditingService, service])

  if (!service) return null;

  const { participants } = service

  const participantsWithoutClothingSelected = Object.values(participants).filter(({selectedValue}) => !selectedValue || selectedValue === service.isNoneOption);

  if (!participantsWithoutClothingSelected.length) return null;

  function toggle() {
    setIsParticipantMenuOpen(!isParticipantMenuOpen);
  }

  return (
    <ListItem.Root>
      <ListItem.Content>
        {participantsWithoutClothingSelected.length > 1 && (
          <Popover.Root open={isParticipantMenuOpen} onOpenChange={toggle}>
            <PopoverTrigger>
              <Typography $variant='body-md-bold'>{intl.formatMessage({ id: 'checkout_steps_clothing_tshirt_add_button_for' })}</Typography>
            </PopoverTrigger>
            <Popover.Portal>
              <PopoverContent align="center" collisionPadding={{ bottom: 85 }}>
                {participantsWithoutClothingSelected.map((participant) => (
                  <Button key={participant.id} $hierarchy="tertiary" $withoutColor onClick={() => handleClick(participant.id)}>
                    <Typography>{intl.formatMessage({id: 'checkout_steps_clothing_participant_menu_item'}, {name: participant.name})}</Typography>
                  </Button>
                ))}
              </PopoverContent>
            </Popover.Portal>
          </Popover.Root>
        )}
        {participantsWithoutClothingSelected.length === 1 && (
          <SelectParticipantButton $hierarchy='secondary' onClick={() => handleClick(participantsWithoutClothingSelected[0].id)} $withoutColor $size='small'>
            <Typography>
              {values(participants).length > 1 && intl.formatMessage({ id: 'checkout_steps_clothing_tshirt_add_button_per_participant' }, {name: participantsWithoutClothingSelected[0].name })}
              {values(participants).length === 1 && intl.formatMessage({ id: 'checkout_steps_clothing_tshirt_add_button' })}
            </Typography>
          </SelectParticipantButton>
        )}
      </ListItem.Content>
    </ListItem.Root>
  )
}

function ClothingServicePerParticipant(participant: CheckoutServiceParticipant) {
  const {selectedValue, name} = participant
  const { service } = useService(ServiceType.TShirt);
  const { sendEvent } = useAnalytics();
  const {isDesktopSm} = useBreakpoints();
  const { startEditingService } = useClothingServiceStore();

  const intl = useIntl();

  const handleClick = React.useCallback(() => {
      if (!participant) return;
      if (!service) return;

      startEditingService({
        participantId: participant.id,
        serviceType: ServiceType.TShirt,
        isEditing: true
      });

      sendEvent(
        createClothingServiceEvent({
          result: 'Change',
          type: participant.name,
          description: 'TShirt',
          element: 'element clicked'
        })
      );
    },
    [sendEvent, startEditingService, service, participant])

  if (!service) return null;

  const { options, participants } = service

  return (
    <>
      { selectedValue && selectedValue !== service.isNoneOption &&
        <ListItem.Root>
          <ListItem.Content>
            {Object.values(participants).length > 1 && <ListItem.Label>
              <ParticipantName as="span" $variant="body-sm">
                {intl.formatMessage({ id: 'checkout_steps_clothing_for_participant' }, {name})}
              </ParticipantName>
            </ListItem.Label>
            }
            <OptionSelectedTitle>
              {intl.formatMessage({ id: 'checkout_steps_clothing_selected_tshirt_option' },
                {
                  gender: intl.formatMessage({ id: `checkout_steps_clothing_sheet_genders_${options[selectedValue].gender.toLowerCase()}`}),
                  size: options[selectedValue].size,
                })}
            </OptionSelectedTitle>
            <ListItem.Label>
              <OptionSelected as="span" $variant="body-sm" />
            </ListItem.Label>
          </ListItem.Content>
          <ListItem.Trailing>
            <PriceBlock>
              <Price as="span" $variant="label">
                { selectedValue ? intl.formatCurrency(options[selectedValue].price, options[selectedValue].price !== 0): ''}
              </Price>
              { !isDesktopSm && <OpenSheetButton $hierarchy='tertiary' $withoutColor $size='small' onClick={handleClick}>
                <Icon glyph="ChevronRight" size={24} />
              </OpenSheetButton>}
              { isDesktopSm && <Button $hierarchy='secondary' onClick={handleClick} $withoutColor $size='small'>
                {intl.formatMessage({ id: 'checkout_steps_clothing_edit_button' })}
              </Button>
              }
            </PriceBlock>
          </ListItem.Trailing>
        </ListItem.Root>
      }
      <ClothingServiceSheet participant={participant}/>
    </>
  );
}

function ClothingServiceSheet({participant}: {participant: CheckoutServiceParticipant; }) {
  const { isTablet } = useBreakpoints();
  const clothingWrapperSide = isTablet ? 'right' : 'bottom' as SheetVariant
  const intl = useIntl();
  const [isFixed, setIsFixed] = React.useState(false);
  const contentWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const { sendEvent } = useAnalytics();

  const {id: participantId, name} = participant
  const { service, setServiceValue} = useService(ServiceType.TShirt);
  const { endEditingService } = useClothingServiceStore();

  const handleSaveValue = React.useCallback(() => {
    if (!service) return;
    const { id } = service.editingSelectedValues[participantId]
    endEditingService(`${ServiceType.TShirt}-${participantId}`)
    setServiceValue(saveEditingClothingServiceValue({ participantId, value: id })(service))
    sendEvent(
      createClothingServiceEvent({
        result: 'Save changes',
        type: '',
        description: 'TShirt',
        element: 'clickable text'
      })
    );
  },[sendEvent, participantId, service, setServiceValue, endEditingService])

  const handleCancelClick = React.useCallback(() => {
      if (!participantId) return;
      if (!service) return;

      endEditingService(`${ServiceType.TShirt}-${participantId}`)

      sendEvent(
        createClothingServiceEvent({
          result: `Cancel`,
          description: 'TShirt',
          element: 'clickable text',
          type: ''
        })
      );
    },
    [sendEvent, service, participantId, endEditingService])

  const isParticipantBeingEdited = useClothingServiceStore((clothingServiceIsBeingEdited(`${ServiceType.TShirt}-${participantId}`)));

  React.useEffect(() => {
    const checkHeight = () => {
      const contentHeight = contentWrapperRef.current?.scrollHeight || 0;
      const clientHeight = contentWrapperRef.current?.clientHeight || 0;
      setIsFixed(contentHeight > clientHeight);
    };

    const resizeObserver = new ResizeObserver(() => {
      checkHeight();
    });

    if (contentWrapperRef.current) {
      resizeObserver.observe(contentWrapperRef.current);
    }

    if (isParticipantBeingEdited) {
      setTimeout(() => {
        checkHeight();
      }, 50);
    }
    window.addEventListener('resize', checkHeight);

    return () => {
      window.removeEventListener('resize', checkHeight);

      if (contentWrapperRef.current) {
        resizeObserver.unobserve(contentWrapperRef.current);
      }
    };
  }, [isParticipantBeingEdited]);

  if (!service) return null;
  const { participants } = service

  return(
    <Sheet modal open={isParticipantBeingEdited}>
      <SheetPortal >
        <SheetOverlay onClick={handleCancelClick}/>
        <SheetContent $side={clothingWrapperSide }>
          <SheetHeader>
            <SheetTitle>{intl.formatMessage({ id: 'checkout_steps_clothing_tshirt_sheet_title' })}</SheetTitle>
            <SheetClose onClick={handleCancelClick}>{intl.formatMessage({ id: 'checkout_steps_clothing_sheet_cancel_button' })}</SheetClose>
          </SheetHeader>
          <ClothingContent ref={contentWrapperRef}>
            <SpaceBetween direction="y" spacing={tk('spacing_md')}>
              <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
                {participants && Object.values(participants).length > 1 && <SheetParticipantName $variant='body-lg-bold'>{intl.formatMessage({ id: 'checkout_steps_ancillaries_clothing_for_participant_label' }, {name})}</SheetParticipantName>}
              </SpaceBetween>
              <SelectOptionFilter participantId={participantId}/>
              <GenderFilter participantId={participantId}/>
              <SizeFilter participantId={participantId} />
            </SpaceBetween>
          </ClothingContent>
          <SheetFooter $isFixed={isFixed}>
            <SpaceBetween direction="x" spacing={tk('spacing_xs')} justifyContent='end'>
              <Button $hierarchy='primary' onClick={handleSaveValue} $fullSize>
                {intl.formatMessage({ id: 'checkout_steps_clothing_accept_text_button' })}
              </Button>
            </SpaceBetween>
          </SheetFooter>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  )
}

function GenderFilter({participantId}: {participantId: string}) {
  const intl = useIntl();
  const { service, setServiceLocalValue } = useService(ServiceType.TShirt);
  const { sendClothingGenderFilterClickEvent } = useClickFilterClothingService(service, 'TShirt');

  const handleGenderChange = React.useCallback((value: string) => {
      if (!value) return;
      setServiceLocalValue(
        updateEditingClothingServiceGender({
          participantId,
          value,
        })
      )

      sendClothingGenderFilterClickEvent(value)
    },
    [sendClothingGenderFilterClickEvent, participantId, setServiceLocalValue]
  );

  if (!service) return null;
  const {gender: genderSelected, id: selectedId} = clothingServiceEditingValueByIdSelector(service, participantId) || {}
  let genders = clothingGenderSelector(service, selectedId)
  if (genders.length === 0) return null;


  genders = genders.filter(gender => GENDERS_ORDER.includes(gender)).sort((a, b) => GENDERS_ORDER.indexOf(a) - GENDERS_ORDER.indexOf(b))

  return <FormField as='div'>
    <SheetFilterLabel><Typography>{intl.formatMessage({ id: 'checkout_steps_clothing_sheet_gender_text' })}</Typography></SheetFilterLabel>
    <FormFieldControl>
      <ToggleGroup type='single' defaultValue={genderSelected} onValueChange={handleGenderChange}>
        {genders.map((gender) => (
          <ToggleGroup.Item key={gender} value={gender} disabled={gender === genderSelected}>
            {intl.formatMessage({ id: `checkout_steps_clothing_sheet_genders_${gender?.toLowerCase()}` })}
          </ToggleGroup.Item>
        ))}
      </ToggleGroup>
    </FormFieldControl>
  </FormField>
}

function SizeFilter({participantId}: {participantId: string}) {
  const intl = useIntl();
  const { service, setServiceLocalValue } = useService(ServiceType.TShirt);
  const { sendClothingSizeFilterClickEvent } = useClickFilterClothingService(service, 'TShirt');

  const handleSizeChange = React.useCallback((value: string) => {
      if (!value) return;
      setServiceLocalValue(
        updateEditingClothingServiceSize({
          participantId,
          value,
        })
      )
      sendClothingSizeFilterClickEvent(value)
    },
    [sendClothingSizeFilterClickEvent, participantId, setServiceLocalValue]
  );

  if (!service) return null;

  const {size: sizeSelected, gender: genderSelected, id: selectedId} = clothingServiceEditingValueByIdSelector(service, participantId) || {}
  let sizes = clothingSizeSelector(service, genderSelected ?? '', selectedId)
  if (sizes.length === 0) return null;

  sizes = sizes.sort((a, b) => SIZES_ORDER.indexOf(a) - SIZES_ORDER.indexOf(b))

  return (
    <FormField as='div'>
      <SheetFilterLabel><Typography>{intl.formatMessage({ id: 'checkout_steps_clothing_sheet_sizes_text' })}</Typography></SheetFilterLabel>
      <FormFieldControl>
        <ToggleGroup type='single' value={sizeSelected} onValueChange={handleSizeChange}>
          {sizes.map((size) => (
            <ToggleGroup.Item key={size} value={size} disabled={size === sizeSelected}>
              {size}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup>
      </FormFieldControl>
    </FormField>
  )

}

function SelectOptionFilter({participantId}: {participantId: string}) {
  const intl = useIntl();
  const { service, setServiceLocalValue } = useService(ServiceType.TShirt);
  const { sendClothingOptionFilterClickEvent } = useClickFilterClothingService(service, 'TShirt');

  const handleValueChange = React.useCallback((value: string) => {
      if (!value) return;
      setServiceLocalValue(
        updateEditingClothingServiceValue({
          participantId,
          value,
        })
      )
      sendClothingOptionFilterClickEvent(value)
    },
    [participantId, setServiceLocalValue]
  );

  if (!service) return null;

  const { options, participants } = service
  const {id: optionSelectedId, size: sizeSelected, gender: genderSelected} = clothingServiceEditingValueByIdSelector(service, participantId) || {}

  const {availableOptions: availableOptionsParticipant} = participants[participantId]

  let allGenders =  Array.from(new Set(Object.values(options).filter(option => option.gender !== NO_GENDER).map(option => option.gender)))
  allGenders = allGenders.sort((a, b) => GENDERS_ORDER.indexOf(a) - GENDERS_ORDER.indexOf(b))
  const gender = (genderSelected && genderSelected !== NO_GENDER) ? genderSelected : allGenders[0]

  let allSizes =  Array.from(new Set(Object.values(options).filter(option => option.size !== NO_SIZE).map(option => option.size)))
  allSizes = allSizes.sort((a, b) => SIZES_ORDER.indexOf(a) - SIZES_ORDER.indexOf(b))
  const size = (sizeSelected && sizeSelected !== NO_SIZE) ? sizeSelected :allSizes[0]

  const availableOptionsForSelectedFilters = clothingServiceOptionsListSelector(service, gender, size)

  return (
    <FormField as='div'>
      <FormFieldControl>
        <RadioGroup.Root value={optionSelectedId ?? null} onValueChange={handleValueChange} >
          <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
            {availableOptionsForSelectedFilters.map((optionId) => {

              if (!availableOptionsParticipant.includes(optionId)) { return }

              const { id, badge, price } = options[optionId];

              return (
                <ListItem.Root key={optionId}>
                  <ListItem.Control>
                    <RadioGroup.Item value={id} />
                  </ListItem.Control>
                  <ListItem.Content>
                    {badge && <ListItem.Badge $state="success">{badge}</ListItem.Badge>}
                    <ListItem.Label>{(optionId === service.isNoneOption)
                      ? intl.formatMessage({ id: 'checkout_steps_clothing_sheet_no_tshirt_option' })
                      : intl.formatMessage({ id: 'checkout_steps_clothing_sheet_tshirt_option' })}
                    </ListItem.Label>
                  </ListItem.Content>
                  <ListItem.Trailing>
                    <Price as="span" $variant="label">
                      {(optionId === service.isNoneOption) ? '' : intl.formatCurrency(price, price !== 0)}
                    </Price>
                  </ListItem.Trailing>
                </ListItem.Root>
              );
            })}
          </SpaceBetween>
        </RadioGroup.Root>
      </FormFieldControl>
    </FormField>
  )
}
